/**
 * A helper that simply imports the client-side dependencies required by useCommonTiptapEditorExtensions,
 * so each consumer doesn't have to.
 */
import { useCallback } from 'react';
import { togglePopup } from '../popupPanel/popupActions';
import { PopupIds } from '../popupPanel/popupConstants';
import useCommonTiptapEditorExtensions, {
    UseCommonTiptapEditorExtensionsArgs,
} from '../../../common/tiptap/hooks/useCommonTiptapEditorExtensions';
import { useSelector } from 'react-redux';
import { getHasLiveCollaborators } from '../../remoteActivity/liveCollaboration/liveCollaborationSelector';
import {
    DEFAULT_EDITOR_SYNC_DEBOUNCE_COUNT,
    DEFAULT_EDITOR_SYNC_DEBOUNCE_TIME,
} from '../../../common/tiptap/extensions/externalStoreSync/ExternalStoreSynchroniser';
import useUpdatedRef from '../../utils/react/useUpdatedRef';

const toggleHyperlinkPopupAction = togglePopup({ popupId: PopupIds.HYPERLINK });

export type UseClientCommonTiptapEditorExtensionsArgs = Omit<
    UseCommonTiptapEditorExtensionsArgs,
    'toggleHyperlinkPopupAction'
>;

const useClientCommonTiptapEditorExtensions = (args: UseClientCommonTiptapEditorExtensionsArgs) => {
    const hasLiveCollaborators = useSelector(getHasLiveCollaborators);
    const hasLiveCollaboratorsRef = useUpdatedRef(hasLiveCollaborators);

    /**
     * Need to use refs here, because the ExternalStoreSynchroniser extension's storage is initialised
     * with options only once - thus we need stable functions that can read the latest value via refs.
     *
     * NOTE: The 5x speedup for both debounce time and count is arbitrary & doesn't need to be aligned.
     *   It's simply "feel" based and can be adjusted later if needed.
     */
    const getDebounceTime = useCallback(
        () =>
            hasLiveCollaboratorsRef.current ? DEFAULT_EDITOR_SYNC_DEBOUNCE_TIME / 5 : DEFAULT_EDITOR_SYNC_DEBOUNCE_TIME,
        [],
    );
    const getDebounceCount = useCallback(
        () =>
            hasLiveCollaboratorsRef.current
                ? DEFAULT_EDITOR_SYNC_DEBOUNCE_COUNT / 5
                : DEFAULT_EDITOR_SYNC_DEBOUNCE_COUNT,
        [],
    );

    return useCommonTiptapEditorExtensions({ toggleHyperlinkPopupAction, getDebounceTime, getDebounceCount, ...args });
};

export default useClientCommonTiptapEditorExtensions;
